/**
 * FlowchartSVG — pure-SVG reconstruction of a DeskeyChart from nodes +
 * connectors. Used as a deterministic fallback when the base64 PNG export
 * stored on `chart.image_url` is missing, corrupted, or too large for the
 * browser to decode efficiently.
 *
 * Why this exists
 * ───────────────
 * Previously the Preview / student dashboard relied solely on the base64
 * PNG. Big diagrams (200KB+ data URLs) sometimes failed to decode inside
 * the preview iframe / dark theme stack, leaving an empty card. By
 * re-rendering directly from the structured `nodes` + `connectors` we
 * always show a real, crisp diagram — and it scales (vector → no blur,
 * no decode cost, copy-friendly).
 *
 * Schema mirrors DeskeyChart's editor:
 *   nodes:      [{ id, type, x, y, width, height, text,
 *                  fill, stroke, strokeWidth, textColor, fontSize }]
 *   connectors: [{ from, to, fromSide?, toSide?, label? }]
 *
 * Node `type`s rendered:
 *   • `rect`       — rounded rectangle (default)
 *   • `diamond`    — decision/branch
 *   • `circle`     — start/end pill
 *   • `parallelogram` — input/output
 *   • anything else falls back to `rect`
 */
import React, { useMemo } from 'react';

const DEFAULT_FILL = '#ECFDF5';
const DEFAULT_STROKE = '#34A853';
const DEFAULT_TEXT = '#065f46';

function nodeCenter(n) {
  const w = n.width || n.w || 140;
  const h = n.height || n.h || 70;
  return { cx: (n.x || 0) + w / 2, cy: (n.y || 0) + h / 2, w, h };
}

function anchorPoint(n, side) {
  const { cx, cy, w, h } = nodeCenter(n);
  const x = n.x || 0, y = n.y || 0;
  if (side === 'top') return { x: cx, y };
  if (side === 'bottom') return { x: cx, y: y + h };
  if (side === 'left') return { x, y: cy };
  if (side === 'right') return { x: x + w, y: cy };
  // Default — pick the closest edge later.
  return { x: cx, y: cy };
}

// Pick which two edges to connect when no explicit sides were saved.
function inferSides(from, to) {
  const fc = nodeCenter(from), tc = nodeCenter(to);
  const dx = tc.cx - fc.cx, dy = tc.cy - fc.cy;
  if (Math.abs(dx) > Math.abs(dy)) {
    return dx > 0 ? ['right', 'left'] : ['left', 'right'];
  }
  return dy > 0 ? ['bottom', 'top'] : ['top', 'bottom'];
}

function NodeShape({ n }) {
  const w = n.width || n.w || 140;
  const h = n.height || n.h || 70;
  const x = n.x || 0, y = n.y || 0;
  const fill = n.fill || DEFAULT_FILL;
  const stroke = n.stroke || DEFAULT_STROKE;
  const strokeWidth = n.strokeWidth || 2;
  const textColor = n.textColor || DEFAULT_TEXT;
  const fontSize = n.fontSize || 14;
  const type = (n.type || 'rect').toLowerCase();

  const cx = x + w / 2, cy = y + h / 2;
  const text = n.text || '';
  // Wrap text by character width (rough heuristic)
  const maxChars = Math.max(6, Math.floor(w / (fontSize * 0.55)));
  const lines = [];
  let line = '';
  text.split(/\s+/).forEach((word) => {
    if (line.length + word.length + 1 <= maxChars) {
      line = line ? `${line} ${word}` : word;
    } else {
      if (line) lines.push(line);
      line = word;
    }
  });
  if (line) lines.push(line);
  // Hard-clip to 4 lines so a runaway label can't overflow the shape.
  const dispLines = lines.slice(0, 4);
  if (lines.length > 4) dispLines[3] = `${dispLines[3].slice(0, maxChars - 1)}…`;
  const lineHeight = fontSize * 1.18;
  const startY = cy - ((dispLines.length - 1) * lineHeight) / 2;

  let shape = null;
  if (type === 'diamond') {
    const pts = [
      `${x + w / 2},${y}`,
      `${x + w},${y + h / 2}`,
      `${x + w / 2},${y + h}`,
      `${x},${y + h / 2}`,
    ].join(' ');
    shape = <polygon points={pts} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
  } else if (type === 'circle' || type === 'oval') {
    shape = <ellipse cx={cx} cy={cy} rx={w / 2} ry={h / 2} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
  } else if (type === 'parallelogram') {
    const skew = w * 0.18;
    const pts = [
      `${x + skew},${y}`,
      `${x + w},${y}`,
      `${x + w - skew},${y + h}`,
      `${x},${y + h}`,
    ].join(' ');
    shape = <polygon points={pts} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
  } else if (type === 'hexagon') {
    const sx = w * 0.15;
    const pts = [
      `${x + sx},${y}`,
      `${x + w - sx},${y}`,
      `${x + w},${y + h / 2}`,
      `${x + w - sx},${y + h}`,
      `${x + sx},${y + h}`,
      `${x},${y + h / 2}`,
    ].join(' ');
    shape = <polygon points={pts} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
  } else {
    // Rounded rectangle (default)
    shape = <rect x={x} y={y} width={w} height={h} rx={10} ry={10} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
  }

  return (
    <g>
      {shape}
      <text
        x={cx}
        y={startY}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={fontSize}
        fontWeight={600}
        fill={textColor}
        style={{ pointerEvents: 'none', userSelect: 'none' }}>
        {dispLines.map((ln, i) => (
          <tspan key={i} x={cx} dy={i === 0 ? 0 : lineHeight}>{ln}</tspan>
        ))}
      </text>
    </g>
  );
}

function Connector({ c, nodesById, accent }) {
  const fromNode = nodesById[c.from];
  const toNode = nodesById[c.to];
  if (!fromNode || !toNode) return null;
  const [fSide, tSide] = (c.fromSide || c.toSide)
    ? [c.fromSide || 'bottom', c.toSide || 'top']
    : inferSides(fromNode, toNode);
  const a = anchorPoint(fromNode, fSide);
  const b = anchorPoint(toNode, tSide);
  // Orthogonal routing — one elbow at the midpoint.
  const midX = (a.x + b.x) / 2;
  const midY = (a.y + b.y) / 2;
  let d;
  if (fSide === 'right' || fSide === 'left') {
    d = `M ${a.x} ${a.y} L ${midX} ${a.y} L ${midX} ${b.y} L ${b.x} ${b.y}`;
  } else {
    d = `M ${a.x} ${a.y} L ${a.x} ${midY} L ${b.x} ${midY} L ${b.x} ${b.y}`;
  }
  const stroke = c.color || accent || '#64748b';
  return (
    <g>
      <path d={d} fill="none" stroke={stroke} strokeWidth={1.8} markerEnd="url(#fcArrow)" strokeLinejoin="round" strokeLinecap="round" />
      {c.label && (
        <g>
          <rect
            x={midX - (c.label.length * 3.4 + 6)}
            y={midY - 9}
            width={c.label.length * 6.8 + 12}
            height={18}
            rx={6}
            fill="#ffffff"
            stroke={stroke}
            strokeOpacity={0.35}
            strokeWidth={1} />
          <text x={midX} y={midY} textAnchor="middle" dominantBaseline="central" fontSize={10.5} fontWeight={600} fill={stroke} style={{ userSelect: 'none' }}>
            {c.label.slice(0, 28)}
          </text>
        </g>
      )}
    </g>
  );
}

export default function FlowchartSVG({ nodes = [], connectors = [], accent = '#0d9488', className = '' }) {
  const { viewBox, nodesById } = useMemo(() => {
    if (!nodes.length) return { viewBox: '0 0 600 300', nodesById: {} };
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    const byId = {};
    nodes.forEach((n) => {
      byId[n.id] = n;
      const x = n.x || 0, y = n.y || 0;
      const w = n.width || n.w || 140, h = n.height || n.h || 70;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x + w > maxX) maxX = x + w;
      if (y + h > maxY) maxY = y + h;
    });
    const pad = 32;
    minX -= pad; minY -= pad; maxX += pad; maxY += pad;
    return { viewBox: `${minX} ${minY} ${maxX - minX} ${maxY - minY}`, nodesById: byId };
  }, [nodes]);

  if (!nodes.length) return null;

  return (
    <svg
      viewBox={viewBox}
      xmlns="http://www.w3.org/2000/svg"
      className={`block w-full h-auto max-h-[600px] ${className}`}
      style={{ background: '#ffffff' }}
      role="img"
      aria-label="Flowchart diagram"
      data-testid="flowchart-svg">
      <defs>
        <marker
          id="fcArrow"
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={accent || '#64748b'} />
        </marker>
      </defs>
      {/* Connectors first so nodes paint on top */}
      <g>
        {connectors.map((c, i) => <Connector key={i} c={c} nodesById={nodesById} accent={accent} />)}
      </g>
      <g>
        {nodes.map((n) => <NodeShape key={n.id} n={n} />)}
      </g>
    </svg>
  );
}
